import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub } from '@fortawesome/free-brands-svg-icons';

/**
 * Header Component - Hero Section
 *
 * This component serves as the main hero/intro section of the website.
 * It includes:
 * - Hero title and subtitle
 * - Interactive business card with flip animation
 * - Call-to-action buttons (Download CV, View Portfolio)
 * - Social media links
 * - Scroll indicator
 *
 * Note: Navigation functionality is handled separately by the Navbar component.
 * This keeps the header focused purely on the hero/landing experience.
 */

// Enhanced CSS custom properties for hero section
const heroStyles = `
  .hero-header {
    --hero-bg: rgba(238, 241, 246, 0.1);
    --hero-text: #0F172A;
    --hero-text-secondary: #334155;
    --hero-accent: #4C5FD7;
    --hero-accent-hover: #3B4CC0;
    --hero-card-bg: rgba(255, 255, 255, 0.15);
    --hero-card-border: rgba(15, 23, 42, 0.1);
    --hero-button-bg: rgba(76, 95, 215, 0.9);
    --hero-button-hover: rgba(59, 76, 192, 1);
    --hero-shadow: rgba(0, 0, 0, 0.2);
    --card-bg-light: rgba(242, 247, 251, 0.95); /* calmer off-white for light mode */
    --card-bg-dark: rgba(247, 249, 252, 0.92);
    --card-text-primary: #0F172A;
    --card-text-secondary: #334155;
    --card-tab-active: rgba(76, 95, 215, 0.12);
    --card-tab-hover: rgba(76, 95, 215, 0.08);
    --card-border: rgba(15, 23, 42, 0.1);
    --card-panel-bg: rgba(247, 249, 252, 0.6);
    --card-contact-bg: rgba(247, 249, 252, 0.8);
    --card-contact-hover: rgba(255, 255, 255, 0.9);
  }

  .dark .hero-header {
    --hero-bg: rgba(12, 18, 32, 0.1);
    --hero-text: #E6EAF2;
    --hero-text-secondary: #B7C0D4;
    --hero-accent: #4C5FD7; /* dark-mode keeps blue accent */
    --hero-accent-hover: #3B4CC0;
    --hero-card-bg: rgba(18, 26, 46, 0.15);
    --hero-card-border: rgba(91, 110, 225, 0.15);
    --hero-button-bg: rgba(91, 110, 225, 0.9);
    --hero-button-hover: rgba(76, 95, 215, 1);
    --hero-shadow: rgba(0, 0, 0, 0.5);
    --card-bg-light: rgba(18, 26, 46, 0.95);
    --card-bg-dark: rgba(12, 18, 32, 0.92);
    --card-text-primary: #E6EAF2;
    --card-text-secondary: #B7C0D4;
    --card-tab-active: rgba(91, 110, 225, 0.16);
    --card-tab-hover: rgba(91, 110, 225, 0.1);
    --card-border: rgba(255, 255, 255, 0.08);
    --card-panel-bg: rgba(18, 26, 46, 0.4);
    --card-contact-bg: rgba(255, 255, 255, 0.08);
    --card-contact-hover: rgba(255, 255, 255, 0.12);
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes typewriter {
    from { width: 0; }
    to { width: 100%; }
  }

  @keyframes blink {
    0%, 50% { border-color: #60a5fa; }
    51%, 100% { border-color: transparent; }
  }

  @keyframes glitch {
    0% { transform: translateX(0); }
    10% { transform: translateX(-2px) skew(-5deg); filter: hue-rotate(90deg); }
    20% { transform: translateX(2px) skew(5deg); filter: hue-rotate(180deg); }
    30% { transform: translateX(-1px) skew(-2deg); filter: hue-rotate(270deg); }
    40% { transform: translateX(1px) skew(2deg); filter: hue-rotate(360deg); }
    50% { transform: translateX(0); filter: hue-rotate(0deg); }
    60% { transform: translateX(-1px); opacity: 0.8; }
    70% { transform: translateX(1px); opacity: 1; }
    80% { transform: translateX(0); text-shadow: 2px 0 #60a5fa, -2px 0 #ef4444; }
    90% { text-shadow: none; }
    100% { transform: translateX(0); }
  }

  @keyframes errorFlash {
    0%, 100% {
      color: rgba(255, 255, 255, 0.8);
      text-shadow: none;
    }
    25% {
      color: #ef4444;
      text-shadow: 0 0 10px #ef4444;
    }
    50% {
      color: #f97316;
      text-shadow: 0 0 15px #f97316;
    }
    75% {
      color: #ef4444;
      text-shadow: 0 0 10px #ef4444;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes deleteEffect {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(0.95);
    }
    100% {
      opacity: 0.4;
      transform: scale(0.9);
    }
  }

  @keyframes slideInUpWelcome {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes cardFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-5px) rotate(0.5deg); }
    50% { transform: translateY(-3px) rotate(-0.3deg); }
    75% { transform: translateY(-7px) rotate(0.2deg); }
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes skillPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.9; }
  }

  @keyframes tabSlide {
    from { transform: translateX(-100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes contactHover {
    0% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-2px) scale(1.02); }
    100% { transform: translateY(-2px) scale(1.02); }
  }

  .card-focus {
    outline: 2px solid var(--hero-accent);
    outline-offset: 2px;
    transition: outline 0.2s ease;
  }

  .skill-tag {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .skill-tag:hover {
    animation: skillPulse 0.6s ease-in-out;
    transform: translateY(-1px);
  }

  .typewriter {
    overflow: hidden;
    border-right: 2px solid var(--hero-accent);
    white-space: nowrap;
    animation: typewriter 4s steps(60) 1s both, blink 1s step-end infinite;
    max-width: fit-content;
    margin: 0 auto;
  }  @media (max-width: 768px) {
    .hero-card {
      width: 95vw !important;
      margin: 16px auto !important;
    }

    .hero-card .tab-content {
      padding: 20px 18px !important;
      height: 360px !important;
    }

    .hero-card .skill-tags {
      gap: 6px !important;
      margin-top: 15px !important;
    }

    .hero-card .contact-item {
      padding: 10px 14px !important;
      gap: 12px !important;
    }

    .hero-card .card-name {
      font-size: 1.15rem !important;
      margin-bottom: 6px !important;
    }

    .hero-card .card-description {
      font-size: 0.9rem !important;
      max-width: 300px !important;
      margin-bottom: 15px !important;
    }

    .hero-card .card-container {
      min-height: 380px !important;
    }
  }

  /* Contact link overrides to ensure contrast in light mode */
  .hero-header .contact-link {
    background: #e2e8f0;
    border: 1px solid var(--card-border);
    color: #1e293b !important;
    opacity: 1 !important;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .hero-header .contact-link .header-label,
  .hero-header .contact-link .header-icon {
    color: inherit !important;
    opacity: 1 !important;
    transition: color 0.15s ease;
  }

  .hero-header .contact-link .header-icon-box {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent; /* make icon box transparent in light mode */
    border: 1px solid rgba(15, 23, 42, 0.06); /* subtle neutral outline */
    color: var(--card-text-primary);
    transition: background 0.15s ease, color 0.15s ease;
  }

  .hero-header .contact-link:hover .header-icon-box {
    background: var(--hero-accent);
    color: #fff !important;
  }

  .dark .hero-header .contact-link .header-icon-box {
    background: transparent;
    color: var(--card-text-primary);
  }


  .hero-header .contact-link:hover {
    background: #cbd5e1;
    color: var(--hero-accent) !important;
  }

  /* Preserve dark mode styles using existing variables */
  .dark .hero-header .contact-link {
    background: var(--card-contact-bg) !important;
    color: var(--card-text-primary) !important;
  }

`;

// Inject styles into head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = heroStyles;
  if (!document.head.querySelector('[data-hero-header-styles]')) {
    styleElement.setAttribute('data-hero-header-styles', 'true');
    document.head.appendChild(styleElement);
  }
}

// ============================================
// CONFIGURABLE PHRASES - Edit here only
// ============================================
const headerPhrases = [
  'Interpretable Deep Learning',
  'Medical & Safety-Critical AI',
  'Explainable Vision Systems',
  'Human-Centered AI Research'
];

// Animation timing configuration (in ms)
const TYPING_CONFIG = {
  typeSpeed: 70,        // Slower, more readable
  deleteSpeed: 40,      // Speed of deleting each character
  pauseAfterType: 2200, // Longer pause to read
  pauseAfterDelete: 400 // Pause before starting next phrase
};

// Time-based greeting helper
const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Welcome';
};

const Header = () => {
  const [isDarkMode, setIsDarkMode] = React.useState(false);

  // Dark mode detection
  React.useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') ||
                    (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    // Listen for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => checkDarkMode();
    mediaQuery.addListener(handleChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  // ============================================
  // TYPING ANIMATION STATE
  // ============================================
  const [greeting] = React.useState(getTimeGreeting);
  const [showContent, setShowContent] = React.useState(false);
  const [phraseIndex, setPhraseIndex] = React.useState(0);
  const [displayText, setDisplayText] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(true);
  const [animationComplete, setAnimationComplete] = React.useState(false);

  // Fade in content after mount
  React.useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // TYPING ANIMATION LOGIC (Clean, Non-Looping)
  // ============================================
  React.useEffect(() => {
    // Animation already complete - do nothing
    if (animationComplete) return;

    const currentPhrase = headerPhrases[phraseIndex];
    const isLastPhrase = phraseIndex === headerPhrases.length - 1;

    let timeout;

    if (isTyping) {
      // TYPING: Add one character at a time
      if (displayText.length < currentPhrase.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentPhrase.slice(0, displayText.length + 1));
        }, TYPING_CONFIG.typeSpeed);
      } else {
        // Finished typing current phrase
        if (isLastPhrase) {
          // Final phrase - stop animation, keep text visible
          timeout = setTimeout(() => {
            setAnimationComplete(true);
          }, 500);
        } else {
          // Not the last phrase - pause then start deleting
          timeout = setTimeout(() => {
            setIsTyping(false);
          }, TYPING_CONFIG.pauseAfterType);
        }
      }
    } else {
      // DELETING: Remove one character at a time
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, TYPING_CONFIG.deleteSpeed);
      } else {
        // Finished deleting - move to next phrase
        timeout = setTimeout(() => {
          setPhraseIndex(prev => prev + 1);
          setIsTyping(true);
        }, TYPING_CONFIG.pauseAfterDelete);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, phraseIndex, animationComplete]);

  // Scroll arrow handler
  const scrollToNext = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="hero-header" style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      paddingTop: '70px',
      paddingBottom: '40px',
      position: 'relative'
    }}>

      {/* Main Academic Profile Container */}
      <div
        className="academic-card"
        style={{
          width: '100%',
          maxWidth: '700px',
          padding: '40px',
          borderRadius: '24px',
          background: `linear-gradient(135deg, var(--card-bg-light) 0%, var(--card-bg-dark) 100%)`,
          backdropFilter: 'blur(20px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.6)',
          border: `1px solid var(--card-border)`,
          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: '24px'
        }}
      >
        {/* Profile Image */}
        <div style={{
          position: 'relative',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            padding: '4px',
            background: 'linear-gradient(135deg, var(--hero-accent), transparent)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <img
              src="assets/images/card_logo.png"
              alt="Mohammad Sayem Chowdhury"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid var(--card-bg-light)',
                backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9'
              }}
            />
          </div>
        </div>

        {/* Identity Block */}
        <div>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            color: 'var(--hero-accent)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '12px'
          }}>
            {greeting}
          </div>

          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            color: 'var(--hero-text)',
            letterSpacing: '-0.03em',
            margin: '0 0 8px 0',
            fontFamily: 'Montserrat, sans-serif',
            lineHeight: 1.1
          }}>
            Mohammad Sayem Chowdhury
          </h1>

          <div style={{
            fontSize: '1.1rem',
            color: 'var(--hero-text-secondary)',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <span>Computer Science Researcher</span>
            <span style={{ opacity: 0.4 }}>•</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '0.9rem', color: 'var(--hero-accent)' }} />
              <span>Chattogram, Bangladesh</span>
            </span>
          </div>
        </div>

        {/* Research Focus (Typing) */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '14px 26px',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          border: '1px solid var(--border)',
          boxShadow: 'none'
        }}>
          <div style={{
            fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
            fontSize: '0.95rem',
            color: 'var(--hero-text)',
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ opacity: 0.6, marginRight: '10px', fontWeight: 500 }}>Research Focus:</span>
            <span style={{
              color: 'var(--primary)',
              fontWeight: 600,
              borderRight: animationComplete ? 'none' : '2px solid var(--primary)',
              paddingRight: '2px'
            }}>
              {displayText}
            </span>
          </div>
        </div>

        {/* Social/Contact Links - Row Layout */}
        <div style={{
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: '8px'
        }}>
          {[
            { icon: faEnvelope, label: 'Email', href: 'mailto:m.sayem.c@gmail.com' },
            { icon: faLinkedin, label: 'LinkedIn', href: 'https://linkedin.com/in/mohammad-sayem-chowdhury' },
            { icon: faGithub, label: 'GitHub', href: 'https://github.com/saayeeem' },
          ].map((item) => (
            <a
              key={item.label}
              className="contact-link"
              href={item.href}
              target={item.href.startsWith('mailto') ? '_self' : '_blank'}
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '12px',
                background: isDarkMode ? 'var(--card-contact-bg)' : '#e2e8f0',
                border: '1px solid var(--card-border)',
                color: isDarkMode ? 'var(--card-text-primary)' : '#1e293b',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.background = isDarkMode ? 'var(--card-contact-hover)' : '#cbd5e1';
                e.currentTarget.style.color = 'var(--hero-accent)';
                e.currentTarget.querySelector('.header-icon').style.color = 'var(--hero-accent)';
                const label = e.currentTarget.querySelector('.header-label');
                if (label) label.style.color = 'var(--hero-accent)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = isDarkMode ? 'var(--card-contact-bg)' : '#e2e8f0';
                e.currentTarget.style.color = isDarkMode ? 'var(--card-text-primary)' : '#1e293b';
                e.currentTarget.querySelector('.header-icon').style.color = isDarkMode ? 'var(--card-text-primary)' : '#1e293b';
                const label = e.currentTarget.querySelector('.header-label');
                if (label) label.style.color = isDarkMode ? 'var(--card-text-primary)' : '#1e293b';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <FontAwesomeIcon icon={item.icon} className="header-icon" style={{ fontSize: '1.1rem', color: isDarkMode ? 'var(--card-text-primary)' : '#1e293b', transition: 'color 0.2s' }} />
              <span
                className="header-label"
                style={{
                  color: isDarkMode ? 'var(--card-text-primary)' : '#1e293b',
                  transition: 'color 0.2s',
                  opacity: 1,
                  fontWeight: 500,
                  // Use !important to override any global or parent style
                  ...(isDarkMode ? {} : { color: '#1e293b !important' })
                }}
              >
                {item.label}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: '40px',
          cursor: 'pointer',
          padding: '10px',
          borderRadius: '50%',
          background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          transition: 'all 0.3s ease',
          animation: 'float 3s ease-in-out infinite'
        }}
        onClick={scrollToNext}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--hero-text-secondary)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: 0.7 }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
    </div>
  );
}

export default Header;
